import type * as T from "three";
import { disposeObject, type Three } from "./useThreeCanvas";

/**
 * Brightness of each emissive panel of the reflected studio (HDR, values above 1 are intended):
 * strong softboxes against black flags are what make dark polished metal read as "gunmetal".
 */
export interface StudioLevels {
  background: [number, number, number];
  topLeft: number;
  right: number;
  backLeft: number;
  frontTop: number;
  frontBottom: number;
}

function buildStudio(THREE: Three, k: StudioLevels) {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(...k.background);
  const panel = (w: number, h: number, pos: [number, number, number], v: number) => {
    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(w, h),
      new THREE.MeshBasicMaterial({ color: new THREE.Color(v, v * 1.01, v * 0.985), side: THREE.DoubleSide }),
    );
    mesh.position.set(...pos);
    mesh.lookAt(0, 0, 0);
    scene.add(mesh);
  };
  panel(9, 4, [-3.5, 6, 4], k.topLeft); // wide curved silver highlight
  panel(3.5, 9, [7, 0.5, 2], k.right);
  panel(3, 8, [-7.5, -0.5, -1.5], k.backLeft); // lights the rim
  panel(16, 5, [0, 4.2, 6], k.frontTop); // front gradient: bright above, dark below
  panel(16, 5, [0, -4.2, 6], k.frontBottom);
  panel(3.2, 14, [-3.2, 0, 6.5], 0); // black flags: the broad dark reflections
  panel(2.4, 14, [4.2, 0, 6], 0);
  panel(18, 2.4, [0, -2.6, 6.5], 0);
  return scene;
}

/** Prefiltered environment map of the studio; the temporary scene and generator are freed. */
export function studioEnvironment(THREE: Three, renderer: T.WebGLRenderer, levels: StudioLevels) {
  const pmrem = new THREE.PMREMGenerator(renderer);
  const studio = buildStudio(THREE, levels);
  const texture = pmrem.fromScene(studio, 0.03).texture;
  disposeObject(studio);
  pmrem.dispose();
  return texture;
}
